import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';
import {
  IActionParamDescriptor,
  ICustomFieldDescriptor,
  IEnumOption,
  IOrderFieldDescriptor,
  IOrderModifications,
} from '../../models/workflows.models';
import { WorkflowsService } from '../../services/workflows.service';

/** Fallback enum field key for the location picker if the descriptor omits its endpoint. */
const DEFAULT_LOCATION_FIELD_KEY = 'order.location';

type ShippingMode = 'absolute' | 'relative';
type LocationMode = 'main' | 'specific';

/** Prefix distinguishing custom-field picker options from standard order fields. */
const CUSTOM_PREFIX = 'cf:';

/**
 * Composite control for the "Modify order details" action.
 *
 * A grouped multi-select field picker chooses which order fields to write;
 * each selected field renders a typed sub-form. The component owns its state
 * and emits the full `modifications` object (see the data contract) on change.
 * It hydrates once from the incoming `value`; subsequent parent updates are
 * echoes of its own emissions and are ignored to avoid a feedback loop.
 */
@Component({
  selector: 'app-workflow-order-modifications',
  templateUrl: './workflow-order-modifications.component.html',
  styleUrls: ['./workflow-order-modifications.component.scss'],
})
export class WorkflowOrderModificationsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() param!: IActionParamDescriptor;
  @Input() enumCache: Record<string, IEnumOption[]> = {};
  @Input() value: any = {};

  @Output() valueChange = new EventEmitter<any>();

  /** Field picker: order-field keys plus `cf:<technicalName>` for custom fields. */
  pickerControl = new FormControl<string[]>([], { nonNullable: true });

  // Standard order-field working state.
  shippingModeControl = new FormControl<ShippingMode>('absolute', { nonNullable: true });
  shippingDateControl = new FormControl<string | null>(null);
  shippingDaysControl = new FormControl<number | null>(null);
  locationModeControl = new FormControl<LocationMode>('main', { nonNullable: true });
  locationValueControl = new FormControl<number | null>(null);
  commentControl = new FormControl<string>('', { nonNullable: true });

  /**
   * Search box backing the specific-location autocomplete. Holds a free-text query
   * while typing and the picked {@link IEnumOption} once a location is selected
   * (mat-autocomplete `displayWith` renders its label). The chosen id is mirrored
   * onto {@link locationValueControl}, which is what {@link buildModel} reads.
   */
  locationSearchControl = new FormControl<string | IEnumOption>('', { nonNullable: true });
  filteredLocationOptions$: Observable<IEnumOption[]> = of([]);

  /** One control per custom field, keyed by technicalName. */
  customControls: Record<string, FormControl> = {};

  private suppressEmit = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private workflowsService: WorkflowsService,
  ) {}

  ngOnInit(): void {
    this.buildCustomControls();
    this.hydrateFromValue(this.value);
    this.wireEmitters();
    this.wireLocationAutocomplete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The enum cache is populated asynchronously by the parent step, so a saved
    // location id may hydrate before its label is available. Re-resolve the label
    // once the cache arrives — display only, so no re-emit is triggered.
    if (changes['enumCache'] && !changes['enumCache'].firstChange) {
      this.refreshSelectedLocationLabel();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Descriptor accessors ───────────────────────────────────────────────

  get orderFields(): IOrderFieldDescriptor[] {
    return this.param?.orderFields ?? [];
  }

  get customFields(): ICustomFieldDescriptor[] {
    return this.param?.customFields ?? [];
  }

  isSelected(key: string): boolean {
    return (this.pickerControl.value ?? []).includes(key);
  }

  isCustomSelected(field: ICustomFieldDescriptor): boolean {
    return this.isSelected(CUSTOM_PREFIX + field.technicalName);
  }

  label(i18nLabel: string): string {
    return this.translate.instant(i18nLabel);
  }

  // ── Options ────────────────────────────────────────────────────────────

  /** Enum field key feeding the location autocomplete (parsed from the descriptor). */
  get locationFieldKey(): string {
    const locationField = this.orderFields.find((field) => field.fieldType === 'location');
    const endpoint = locationField?.optionsEndpoint ?? '';
    return endpoint.match(/field=([^&]+)/)?.[1] ?? DEFAULT_LOCATION_FIELD_KEY;
  }

  customSelectOptions(field: ICustomFieldDescriptor): SelectOption[] {
    return (field.allowedValues ?? []).map((value) => ({ label: value, value }));
  }

  // ── Location autocomplete ────────────────────────────────────────────────

  /** Render a location option (or free-text query) as its display label. */
  displayLocationOption(option: string | IEnumOption | null): string {
    if (!option) {
      return '';
    }
    return typeof option === 'string' ? option : option.label;
  }

  /** Commit an autocomplete pick: mirror the id onto the model-backing control. */
  selectLocationOption(option: IEnumOption): void {
    this.mergeLocationOptions([option]);
    // Setting the id emits the updated model via the wired locationValueControl.
    this.locationValueControl.setValue(Number(option.value));
  }

  private wireLocationAutocomplete(): void {
    this.filteredLocationOptions$ = this.locationSearchControl.valueChanges.pipe(
      startWith(this.locationSearchControl.value),
      debounceTime(200),
      distinctUntilChanged(
        (previous, current) =>
          this.normalizeLocationSearch(previous) === this.normalizeLocationSearch(current)
      ),
      switchMap((value) => this.loadLocationSuggestions(this.normalizeLocationSearch(value))),
      takeUntil(this.destroy$),
    );
  }

  private loadLocationSuggestions(search: string): Observable<IEnumOption[]> {
    return this.workflowsService.searchEnumValues(this.locationFieldKey, search).pipe(
      tap((options) => this.mergeLocationOptions(options)),
      catchError(() => of([])),
    );
  }

  private normalizeLocationSearch(value: string | IEnumOption): string {
    return typeof value === 'string' ? value.trim() : value?.label?.trim() ?? '';
  }

  /** Seed the autocomplete display from a saved id, resolving its label if cached. */
  private seedSelectedLocation(value: number | null): void {
    if (value == null) {
      this.locationSearchControl.setValue('', { emitEvent: false });
      return;
    }
    const option = this.findCachedLocation(value) ?? { value, label: String(value) };
    this.locationSearchControl.setValue(option, { emitEvent: false });
  }

  /** Upgrade a raw-id display to its resolved label once the enum cache fills. */
  private refreshSelectedLocationLabel(): void {
    const value = this.locationValueControl.value;
    if (value == null) {
      return;
    }
    const current = this.locationSearchControl.value;
    const alreadyLabeled = typeof current !== 'string' && current?.label !== String(value);
    if (alreadyLabeled) {
      return;
    }
    const cached = this.findCachedLocation(value);
    if (cached) {
      this.locationSearchControl.setValue(cached, { emitEvent: false });
    }
  }

  private findCachedLocation(value: number): IEnumOption | undefined {
    return (this.enumCache[this.locationFieldKey] ?? []).find((option) =>
      this.isSameValue(option.value, value)
    );
  }

  private mergeLocationOptions(options: IEnumOption[]): void {
    if (!options.length) {
      return;
    }
    const key = this.locationFieldKey;
    const merged = [...(this.enumCache[key] ?? [])];
    for (const option of options) {
      if (!merged.some((existing) => this.isSameValue(existing.value, option.value))) {
        merged.push(option);
      }
    }
    this.enumCache = { ...this.enumCache, [key]: merged };
  }

  private isSameValue(optionValue: string | number, currentValue: unknown): boolean {
    return optionValue === currentValue || String(optionValue) === String(currentValue);
  }

  // ── Hydration & wiring ─────────────────────────────────────────────────

  private buildCustomControls(): void {
    this.customControls = {};
    for (const field of this.customFields) {
      const initial = field.fieldType === 'multi_select' ? [] : null;
      this.customControls[field.technicalName] = new FormControl(initial);
    }
  }

  private hydrateFromValue(value: any): void {
    this.suppressEmit = true;
    const model = value && typeof value === 'object' ? value : {};
    const keys: string[] = [];

    const shipping = model.shipping_date;
    if (shipping) {
      keys.push('shipping_date');
      if (shipping.mode === 'relative') {
        this.shippingModeControl.setValue('relative', { emitEvent: false });
        this.shippingDaysControl.setValue(shipping.days ?? null, { emitEvent: false });
      } else {
        this.shippingModeControl.setValue('absolute', { emitEvent: false });
        this.shippingDateControl.setValue(shipping.value ?? null, { emitEvent: false });
      }
    }

    const location = model.partner_id;
    if (location) {
      keys.push('partner_id');
      if (location.mode === 'specific') {
        this.locationModeControl.setValue('specific', { emitEvent: false });
        this.locationValueControl.setValue(location.value ?? null, { emitEvent: false });
        this.seedSelectedLocation(location.value ?? null);
      } else {
        this.locationModeControl.setValue('main', { emitEvent: false });
      }
    }

    if (typeof model.comment === 'string') {
      keys.push('comment');
      this.commentControl.setValue(model.comment, { emitEvent: false });
    }

    const customValues = model.custom_fields ?? {};
    for (const technicalName of Object.keys(customValues)) {
      const control = this.customControls[technicalName];
      if (control) {
        keys.push(CUSTOM_PREFIX + technicalName);
        control.setValue(customValues[technicalName], { emitEvent: false });
      }
    }

    this.pickerControl.setValue(keys, { emitEvent: false });
    this.suppressEmit = false;
  }

  private wireEmitters(): void {
    const controls: FormControl[] = [
      this.pickerControl,
      this.shippingModeControl,
      this.shippingDateControl,
      this.shippingDaysControl,
      this.locationModeControl,
      this.locationValueControl,
      this.commentControl,
      ...Object.values(this.customControls),
    ];
    for (const control of controls) {
      control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.emit());
    }
  }

  private emit(): void {
    if (this.suppressEmit) return;
    this.valueChange.emit(this.buildModel());
  }

  private buildModel(): IOrderModifications {
    const model: IOrderModifications = {};

    if (this.isSelected('shipping_date')) {
      model.shipping_date =
        this.shippingModeControl.value === 'relative'
          ? { mode: 'relative', days: Number(this.shippingDaysControl.value) || 0 }
          : { mode: 'absolute', value: this.shippingDateControl.value ?? null };
    }

    if (this.isSelected('partner_id')) {
      model.partner_id =
        this.locationModeControl.value === 'specific'
          ? { mode: 'specific', value: this.locationValueControl.value ?? null }
          : { mode: 'main' };
    }

    if (this.isSelected('comment')) {
      model.comment = this.commentControl.value ?? '';
    }

    const customFields: Record<string, any> = {};
    for (const field of this.customFields) {
      if (this.isCustomSelected(field)) {
        customFields[field.technicalName] = this.customControls[field.technicalName]?.value ?? null;
      }
    }
    if (Object.keys(customFields).length) {
      model.custom_fields = customFields;
    }

    return model;
  }
}
